import React, { useState, useEffect, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { QuickFilter } from "../../components";
import { ModalContext, SettingsContext } from "../../contexts";
import { ProductSearchHelper } from "../../helpers";
import SearchFilters from "../../views/Search/Filters";
import FilterBullets from "../../views/Search/FilterBullets";
import { Button, Icon, Toggle } from "../../components/ui";
import { symbolFor } from "../../core/currency";
import { OWNED_BY_FILTER_LABELS } from "../constants";

export const convertShopFiltersToManageListings = (shopFilters) => {
  const manageListingsFilters = {};

  Object.keys(shopFilters).forEach((filterKey) => {
    if (filterKey === "category_id") {
      manageListingsFilters.category_id = shopFilters[filterKey];
    } else if (
      filterKey === "brand_id" ||
      filterKey === "model_id" ||
      // internal_product is an ENUM, so the ids go over the wire, not the
      // display names. Its own key deliberately — `in_stock` is rewritten
      // unconditionally from the In Stock / Consignment pills in
      // useListingList, and would silently stomp this.
      filterKey === "internal_product"
    ) {
      if (shopFilters[filterKey] && shopFilters[filterKey].length > 0) {
        manageListingsFilters[filterKey] = shopFilters[filterKey].map(
          (item) => item.id
        );
      }
    } else if (
      filterKey === "condition" ||
      filterKey === "year" ||
      filterKey === "location" ||
      filterKey === "case_material" ||
      filterKey === "case_diameter" ||
      filterKey === "dial_color" ||
      filterKey === "color"
    ) {
      if (shopFilters[filterKey] && shopFilters[filterKey].length > 0) {
        manageListingsFilters[filterKey] = shopFilters[filterKey].map(
          (item) => item.name
        );
      }
    } else if (filterKey === "price") {
      if (shopFilters[filterKey] && shopFilters[filterKey].length > 0) {
        const priceFilter = shopFilters[filterKey][0];
        if (priceFilter.id && priceFilter.id.startsWith("range:")) {
          const range = priceFilter.id.substr(6).split("-");
          manageListingsFilters.price = {
            from: parseInt(range[0]) * 100,
            to: parseInt(range[1]) * 100,
          };
        }
      }
    } else if (filterKey === "status") {
      if (shopFilters[filterKey] && shopFilters[filterKey].length > 0) {
        const statusValue = shopFilters[filterKey][0].id;
        if (statusValue === "reserved") {
          manageListingsFilters.is_reserved = true;
        } else if (statusValue === "deposit") {
          manageListingsFilters.with_deposit = true;
        } else {
          manageListingsFilters.status = [statusValue];
        }
      }
    } else if (filterKey === "store") {
      // STORE filter → backend `marketplaces: {account_ids, ebay, wf}` shape.
      // Option ids are "acc:<id>" / "ebay" / "wf" (see RCFilters.jsx).
      const picks = shopFilters[filterKey];
      if (Array.isArray(picks) && picks.length > 0) {
        const accountIds = [];
        let includeEbay = false;
        let includeWf = false;
        picks.forEach((p) => {
          if (typeof p?.id !== "string") return;
          if (p.id === "ebay") includeEbay = true;
          else if (p.id === "wf") includeWf = true;
          else if (p.id.startsWith("acc:")) {
            const n = parseInt(p.id.slice(4), 10);
            if (Number.isFinite(n)) accountIds.push(n);
          }
        });
        manageListingsFilters.marketplaces = {
          account_ids: accountIds,
          ebay: includeEbay,
          wf: includeWf,
        };
      }
    }
  });

  return manageListingsFilters;
};

export const convertManageListingsToShopFilters = (manageListingsFilters) => {
  const shopFilters = {};

  if (!manageListingsFilters) {
    return shopFilters;
  }

  Object.keys(manageListingsFilters).forEach((filterKey) => {
    if (filterKey === "category_id") {
      shopFilters[filterKey] = manageListingsFilters[filterKey];
    } else if (filterKey === "brand_id" || filterKey === "model_id") {
      if (
        manageListingsFilters[filterKey] &&
        manageListingsFilters[filterKey].length > 0
      ) {
        shopFilters[filterKey] = manageListingsFilters[filterKey].map((id) => ({
          id: id,
          name: `${filterKey === "brand_id" ? "Brand" : "Model"}: ${id}`,
        }));
      }
    } else if (
      filterKey === "condition" ||
      filterKey === "year" ||
      filterKey === "location" ||
      filterKey === "case_material" ||
      filterKey === "case_diameter" ||
      filterKey === "dial_color" ||
      filterKey === "color"
    ) {
      if (
        manageListingsFilters[filterKey] &&
        manageListingsFilters[filterKey].length > 0
      ) {
        shopFilters[filterKey] = manageListingsFilters[filterKey].map(
          (name) => ({
            id: name,
            name: name,
          })
        );
      }
    } else if (filterKey === "internal_product") {
      // Enum ids in, {id, name} out. Names are best-effort for the chips — the
      // RCFilters list replaces them with the live server-fed labels (which
      // carry counts) on next open.
      if (
        manageListingsFilters[filterKey] &&
        manageListingsFilters[filterKey].length > 0
      ) {
        shopFilters[filterKey] = manageListingsFilters[filterKey].map((id) => ({
          id,
          name: OWNED_BY_FILTER_LABELS[id] ?? id,
        }));
      }
    } else if (filterKey === "price") {
      if (
        manageListingsFilters[filterKey] &&
        manageListingsFilters[filterKey].from &&
        manageListingsFilters[filterKey].to
      ) {
        const from = Math.floor(manageListingsFilters[filterKey].from / 100);
        const to = Math.floor(manageListingsFilters[filterKey].to / 100);
        // TODO(multicurrency): listing.currency once API exposes it here
        const currencySymbol = symbolFor("gbp");
        shopFilters[filterKey] = [
          {
            id: `range:${from}-${to}`,
            name: `${currencySymbol}${from} - ${currencySymbol}${to}`,
          },
        ];
      }
    } else if (filterKey === "status") {
      if (
        manageListingsFilters[filterKey] &&
        manageListingsFilters[filterKey].length > 0
      ) {
        shopFilters[filterKey] = manageListingsFilters[filterKey].map(
          (status) => ({
            id: status,
            name: status.charAt(0).toUpperCase() + status.slice(1),
          })
        );
      }
    } else if (filterKey === "is_reserved") {
      if (manageListingsFilters[filterKey]) {
        shopFilters.status = [
          {
            id: "reserved",
            name: "Reserved",
          },
        ];
      }
    } else if (filterKey === "with_deposit") {
      if (manageListingsFilters[filterKey]) {
        shopFilters.status = [
          {
            id: "deposit",
            name: "With Deposit",
          },
        ];
      }
    } else if (filterKey === "in_stock") {
      if (manageListingsFilters[filterKey] === "owned") {
        shopFilters[filterKey] = [
          {
            id: "owned",
            name: "In Stock",
          },
        ];
      }
    } else if (filterKey === "missing_competitive_analysis") {
      if (manageListingsFilters[filterKey]) {
        shopFilters[filterKey] = [
          {
            id: "true",
            name: "Missing Competitive Analysis",
          },
        ];
      }
    } else if (filterKey === "brand_name") {
      if (manageListingsFilters[filterKey]) {
        shopFilters[filterKey] = [
          {
            id: manageListingsFilters[filterKey],
            name: `Brand: ${manageListingsFilters[filterKey]}`,
          },
        ];
      }
    } else if (filterKey === "marketplaces") {
      // Reverse the STORE-filter encoding. Names are best-effort (the
      // RCFilters list will replace them with live account labels on
      // next open) — what matters here is that ids round-trip cleanly.
      const mp = manageListingsFilters[filterKey] || {};
      const out = [];
      (mp.account_ids || []).forEach((id) => {
        out.push({ id: `acc:${id}`, name: `Account ${id}` });
      });
      if (mp.ebay) out.push({ id: "ebay", name: "eBay" });
      if (mp.wf)   out.push({ id: "wf",   name: "Watchfinder" });
      if (out.length) shopFilters.store = out;
    }
  });

  return shopFilters;
};

const ListingSearch = ({
  className = "",
  placeholder = "",
  onUpdate,
  onFiltersChange,
  currentFilters = null,
  inStock,
  setInStock,
  isConsignment,
  setConsignment,
}) => {
  const [filtersShown, setFiltersShown] = useState(false);
  const { setSearchShown } = useContext(ModalContext);
  const { isMerchant } = useContext(SettingsContext);

  useEffect(() => {
    setSearchShown(filtersShown);
  }, [filtersShown]);

  const history = useHistory();
  const location = useLocation();

  const search = ProductSearchHelper.parseUrl(location, false);
  const [inputText, setInputText] = useState(search.query);

  const displayFilters = convertManageListingsToShopFilters(currentFilters);

  const onSubmit = (e) => {
    e.preventDefault();
    setFiltersShown(false);
    search.query = inputText;
  };

  const onFilterByClick = (e) => {
    e.preventDefault();
    setFiltersShown(!filtersShown);
  };

  const onFiltersApplied = (searchData) => {
    setFiltersShown(false);
    const manageListingsFilters = convertShopFiltersToManageListings(
      searchData.filters
    );
    if (onFiltersChange) {
      onFiltersChange(manageListingsFilters);
    }
  };

  const onBulletRemoved = (option) => {
    const { filter, id } = option;
    const updatedFilters = currentFilters ? { ...currentFilters } : {};

    if (filter === "status") {
      if (id === "reserved") {
        delete updatedFilters.is_reserved;
        if (updatedFilters.status) {
          updatedFilters.status = updatedFilters.status.filter(
            (s) => s !== "reserved"
          );
          if (updatedFilters.status.length === 0) {
            delete updatedFilters.status;
          }
        }
      } else if (id === "deposit") {
        delete updatedFilters.with_deposit;
        if (updatedFilters.status) {
          updatedFilters.status = updatedFilters.status.filter(
            (s) => s !== "deposit"
          );
          if (updatedFilters.status.length === 0) {
            delete updatedFilters.status;
          }
        }
      } else {
        if (updatedFilters.status) {
          updatedFilters.status = updatedFilters.status.filter((s) => s !== id);
          if (updatedFilters.status.length === 0) {
            delete updatedFilters.status;
          }
        }
      }
    } else if (filter === "in_stock" && id === "owned") {
      delete updatedFilters.in_stock;
    } else if (filter === "internal_product") {
      // Array of enum id strings — drop the one chip, drop the key when empty.
      if (Array.isArray(updatedFilters[filter])) {
        updatedFilters[filter] = updatedFilters[filter].filter((v) => v !== id);
        if (updatedFilters[filter].length === 0) delete updatedFilters[filter];
      }
    } else if (filter === "missing_competitive_analysis" && id === "true") {
      delete updatedFilters.missing_competitive_analysis;
    } else if (filter === "brand_name") {
      delete updatedFilters.brand_name;
    } else if (filter === "brand_id" || filter === "model_id") {
      if (updatedFilters[filter] && Array.isArray(updatedFilters[filter])) {
        updatedFilters[filter] = updatedFilters[filter].filter((item) => {
          if (typeof item === "string") {
            return item !== id.toString();
          } else if (typeof item === "number") {
            return item !== parseInt(id);
          }
          return true;
        });

        if (updatedFilters[filter].length === 0) {
          delete updatedFilters[filter];
        }
      }
    } else {
      if (updatedFilters[filter]) {
        if (Array.isArray(updatedFilters[filter])) {
          updatedFilters[filter] = updatedFilters[filter].filter((item) => {
            if (typeof item === "string") {
              return item !== id;
            } else if (typeof item === "object" && item.id) {
              return item.id !== id;
            }
            return true;
          });

          if (updatedFilters[filter].length === 0) {
            delete updatedFilters[filter];
          }
        } else if (typeof updatedFilters[filter] === "object") {
          if (filter === "price") {
            delete updatedFilters[filter];
          }
        } else if (typeof updatedFilters[filter] === "boolean") {
          delete updatedFilters[filter];
        } else if (typeof updatedFilters[filter] === "string") {
          delete updatedFilters[filter];
        }
      }
    }

    if (onFiltersChange) {
      onFiltersChange(
        Object.keys(updatedFilters).length > 0 ? updatedFilters : null
      );
    }
  };

  const onHanldeConsignment = () => {
    if (isConsignment) {
      setConsignment(!isConsignment);
    } else {
      setInStock(false);
      setConsignment(!isConsignment);
    }
  };

  const onHanldeStock = () => {
    if (inStock) {
      setInStock(!inStock);
    } else {
      setConsignment(false);
      setInStock(!inStock);
    }
  };

  return (
    <div className="mt-0">
      <div className="d-flex justify-content-between content">
        <div className="d-flex align-items-center">
          <QuickFilter
            className={className}
            placeholder={placeholder}
            onUpdate={onUpdate}
          />
          <Button
            variant="secondary"
            className="ms-3"
            data-btn="listing-filter"
            leadingIcon={<Icon name="SlidersHorizontal" size="sm" />}
            aria-expanded={filtersShown}
            onClick={onFilterByClick}
          >
            Filter by
          </Button>
        </div>

        <div className="d-flex align-items-center gap-3">
          {!isMerchant && (
            <Toggle
              label="Consignment"
              data-btn="listing-consignment"
              onChange={onHanldeConsignment}
              checked={isConsignment}
            />
          )}

          {!isMerchant && (
            <Toggle
              label="In stock"
              data-btn="listing-in-stock"
              onChange={onHanldeStock}
              checked={inStock}
            />
          )}
        </div>

        {filtersShown && (
          <SearchFilters
            active={filtersShown}
            search={{
              ...search,
              list: "uploaded",
              filters: displayFilters,
            }}
            onApply={onFiltersApplied}
            close={() => setFiltersShown(false)}
            includeStatusFilter={true}
            list="uploaded"
          />
        )}
      </div>
    </div>
  );
};

export default ListingSearch;
